import { z } from 'zod';

/** Money: decimal string (never floats in SQL) + ISO currency. Numbers are accepted on input. */
export const Money = z.object({
  value: z.union([z.string().regex(/^-?\d{1,15}(\.\d{1,4})?$/), z.number().finite()]).transform((v) => {
    if (typeof v === 'string') return v;
    return Number.isInteger(v) ? String(v) : v.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  }),
  currency: z.string().regex(/^[A-Z]{3}$/),
});
export type Money = { value: string; currency: string };

export const Address = z.object({
  street: z.string().max(200).nullish(),
  postalCode: z.string().max(20).nullish(),
  city: z.string().max(120).nullish(),
  region: z.string().max(120).nullish(),
  country: z.string().max(120).nullish(),
});
export type Address = z.infer<typeof Address>;

export const PersonName = z.object({
  first: z.string().max(120).nullish(),
  last: z.string().max(120).nullish(),
  salutation: z.string().max(40).nullish(),
});
export type PersonName = z.infer<typeof PersonName>;

export const FileRef = z.object({
  blobId: z.string().min(1).max(200),
  name: z.string().min(1).max(300),
  size: z.number().int().nonnegative(),
  mime: z.string().max(200),
});
export type FileRef = z.infer<typeof FileRef>;

/** Provenance source ids (SPEC §7). */
export const SourceId = z
  .string()
  .max(200)
  .regex(/^(manual|formula|rollup|seed|(import|workflow|connector|enrichment|extension|form|ai):[^\s].*)$/, {
    error: 'Invalid source id',
  });
export type SourceId = z.infer<typeof SourceId>;

// -- typed value errors (typed-values) -------------------------------------------------------

/**
 * Stable codes of value and validation errors. Codecs return them from `parse`, field value
 * schemas attach them to their issues, and the API returns them in `issues[].code` with
 * `params` and a message in the member's language.
 */
export const VALUE_ERROR_CODES = [
  'required',
  'invalid_value',
  'invalid_type',
  'invalid_number',
  'invalid_integer',
  'too_small',
  'too_large',
  'too_short',
  'too_long',
  'too_few_items',
  'too_many_items',
  'invalid_currency',
  'currency_not_allowed',
  'invalid_percent',
  'invalid_date',
  'invalid_datetime',
  'invalid_time',
  'invalid_duration',
  'invalid_email',
  'invalid_url',
  'invalid_domain',
  'invalid_phone',
  'invalid_boolean',
  'unknown_option',
  'unknown_member',
  'unknown_record',
  'invalid_rating',
  'invalid_json',
  'invalid_format',
  'invalid_time_zone',
  'unknown_key',
  'read_only',
  'not_parseable',
] as const;
export type ValueErrorCode = (typeof VALUE_ERROR_CODES)[number];

export type ValueErrorParams = Record<string, string | number>;

/** A typed parse or validation error: `{ code: 'too_large', params: { max: 1000 } }`. */
export type ValueError = { code: ValueErrorCode; params?: ValueErrorParams };

/** A validation issue with a stable code (a `ValidationIssue` plus `code` and `params`). */
export type TypedValidationIssue = {
  path: string;
  message: string;
  code: ValueErrorCode;
  params?: ValueErrorParams;
};

export function isValueErrorCode(v: unknown): v is ValueErrorCode {
  return typeof v === 'string' && (VALUE_ERROR_CODES as readonly string[]).includes(v);
}

type MessageLanguage = 'en' | 'de';

const VALUE_ERROR_MESSAGES: Record<MessageLanguage, Record<ValueErrorCode, string>> = {
  en: {
    required: 'Enter a value',
    invalid_value: 'This value is not valid',
    invalid_type: 'Expected {expected}',
    invalid_number: 'Enter a number, for example {example}',
    invalid_integer: 'Enter a whole number',
    too_small: 'Must be at least {min}',
    too_large: 'Must be at most {max}',
    too_short: 'Use at least {min} characters',
    too_long: 'Use at most {max} characters',
    too_few_items: 'Choose at least {min}',
    too_many_items: 'Choose at most {max}',
    invalid_currency: 'Enter an amount, for example {example}',
    currency_not_allowed: 'Only {currency} is allowed here',
    invalid_percent: 'Enter a percentage, for example {example}',
    invalid_date: 'Enter a date, for example {example}',
    invalid_datetime: 'Enter a date and time, for example {example}',
    invalid_time: 'Enter a time, for example {example}',
    invalid_duration: 'Enter a duration, for example 1:30 or 2h 15m',
    invalid_email: 'Enter an email address like name@example.com',
    invalid_url: 'Enter a web address like example.com',
    invalid_domain: 'Enter a domain like example.com',
    invalid_phone: 'Enter a phone number like {example}',
    invalid_boolean: 'Enter yes or no',
    unknown_option: '“{value}” is not one of the options',
    unknown_member: 'No member matches “{value}”',
    unknown_record: 'No record matches “{value}”',
    invalid_rating: 'Choose a rating from 1 to {max}',
    invalid_json: 'Enter valid JSON',
    invalid_format: 'This value has the wrong format',
    invalid_time_zone: 'Unknown time zone',
    unknown_key: 'Unknown field “{key}”',
    read_only: 'This value is calculated and cannot be edited',
    not_parseable: 'This value cannot be typed in; pick it instead',
  },
  de: {
    required: 'Geben Sie einen Wert ein',
    invalid_value: 'Dieser Wert ist ungültig',
    invalid_type: 'Erwartet: {expected}',
    invalid_number: 'Geben Sie eine Zahl ein, zum Beispiel {example}',
    invalid_integer: 'Geben Sie eine ganze Zahl ein',
    too_small: 'Muss mindestens {min} sein',
    too_large: 'Darf höchstens {max} sein',
    too_short: 'Mindestens {min} Zeichen',
    too_long: 'Höchstens {max} Zeichen',
    too_few_items: 'Wählen Sie mindestens {min} aus',
    too_many_items: 'Wählen Sie höchstens {max} aus',
    invalid_currency: 'Geben Sie einen Betrag ein, zum Beispiel {example}',
    currency_not_allowed: 'Hier ist nur {currency} erlaubt',
    invalid_percent: 'Geben Sie einen Prozentwert ein, zum Beispiel {example}',
    invalid_date: 'Geben Sie ein Datum ein, zum Beispiel {example}',
    invalid_datetime: 'Geben Sie Datum und Uhrzeit ein, zum Beispiel {example}',
    invalid_time: 'Geben Sie eine Uhrzeit ein, zum Beispiel {example}',
    invalid_duration: 'Geben Sie eine Dauer ein, zum Beispiel 1:30 oder 2 Std. 15 Min.',
    invalid_email: 'Geben Sie eine E-Mail-Adresse wie name@beispiel.de ein',
    invalid_url: 'Geben Sie eine Webadresse wie beispiel.de ein',
    invalid_domain: 'Geben Sie eine Domain wie beispiel.de ein',
    invalid_phone: 'Geben Sie eine Telefonnummer wie {example} ein',
    invalid_boolean: 'Geben Sie ja oder nein ein',
    unknown_option: '„{value}“ ist keine der Optionen',
    unknown_member: 'Kein Mitglied passt zu „{value}“',
    unknown_record: 'Kein Datensatz passt zu „{value}“',
    invalid_rating: 'Wählen Sie eine Bewertung von 1 bis {max}',
    invalid_json: 'Geben Sie gültiges JSON ein',
    invalid_format: 'Dieser Wert hat das falsche Format',
    invalid_time_zone: 'Unbekannte Zeitzone',
    unknown_key: 'Unbekanntes Feld „{key}“',
    read_only: 'Dieser Wert wird berechnet und kann nicht bearbeitet werden',
    not_parseable: 'Dieser Wert kann nicht eingetippt werden; wählen Sie ihn aus',
  },
};

/** Default examples shown in messages when the caller passes none. */
const DEFAULT_EXAMPLES: Record<MessageLanguage, Partial<Record<ValueErrorCode, string>>> = {
  en: {
    invalid_number: '1,234.5',
    invalid_currency: '1,234.50',
    invalid_percent: '12.5%',
    invalid_date: '09/29/2026',
    invalid_datetime: '09/29/2026 14:30',
    invalid_time: '14:30',
    invalid_phone: '+49 421 5550140',
  },
  de: {
    invalid_number: '1.234,5',
    invalid_currency: '1.234,50',
    invalid_percent: '12,5 %',
    invalid_date: '29.09.2026',
    invalid_datetime: '29.09.2026 14:30',
    invalid_time: '14:30',
    invalid_phone: '+49 421 5550140',
  },
};

const TYPE_NAMES: Record<MessageLanguage, Record<string, string>> = {
  en: {
    string: 'text',
    number: 'a number',
    boolean: 'yes or no',
    array: 'a list',
    object: 'an object',
    record: 'an object',
    map: 'an object',
    tuple: 'a list',
    set: 'a list',
    int: 'a whole number',
    bigint: 'a whole number',
    date: 'a date',
    null: 'no value',
    file: 'a file',
    enum: 'one of the options',
    literal: 'a fixed value',
  },
  de: {
    string: 'Text',
    number: 'eine Zahl',
    boolean: 'ja oder nein',
    array: 'eine Liste',
    object: 'ein Objekt',
    record: 'ein Objekt',
    map: 'ein Objekt',
    tuple: 'eine Liste',
    set: 'eine Liste',
    int: 'eine ganze Zahl',
    bigint: 'eine ganze Zahl',
    date: 'ein Datum',
    null: 'kein Wert',
    file: 'eine Datei',
    enum: 'eine der Optionen',
    literal: 'ein fester Wert',
  },
};

/** Expected types without a readable name (`union`, `custom` …) read as “a valid value”. */
const ANY_TYPE: Record<MessageLanguage, string> = {
  en: 'a valid value',
  de: 'ein gültiger Wert',
};

/** The message of a value error in a language (`en` or `de`; other languages fall back to `en`). */
export function valueErrorMessage(
  code: ValueErrorCode,
  params: ValueErrorParams = {},
  language: string = 'en',
): string {
  const lang: MessageLanguage = language === 'de' ? 'de' : 'en';
  const template = VALUE_ERROR_MESSAGES[lang][code] ?? VALUE_ERROR_MESSAGES[lang].invalid_value;
  const tag = lang === 'de' ? 'de-DE' : 'en-US';
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    let v = params[name];
    if (v === undefined && name === 'example') v = DEFAULT_EXAMPLES[lang][code] ?? '';
    if (name === 'expected' && typeof v === 'string') v = TYPE_NAMES[lang][v] ?? ANY_TYPE[lang];
    if (typeof v === 'number') return new Intl.NumberFormat(tag, { maximumFractionDigits: 6 }).format(v);
    return v === undefined ? '' : String(v);
  });
}

// The language messages are produced in. The browser always passes it explicitly; the API
// installs a resolver that reads the language of the member behind the current request.
let messageLanguageResolver: () => string | undefined = () => undefined;

/** Install the resolver of the current message language (the API does this once at startup). */
export function setMessageLanguageResolver(fn: () => string | undefined): void {
  messageLanguageResolver = fn;
}

/** The language of the current request (server) or `en`. */
export function currentMessageLanguage(): MessageLanguage {
  return messageLanguageResolver() === 'de' ? 'de' : 'en';
}

const PERMISSION_DENIED: Record<MessageLanguage, string> = {
  en: 'You do not have permission to do this. An administrator can give you access.',
  de: 'Dafür fehlt Ihnen die Berechtigung. Eine Administratorin oder ein Administrator kann sie Ihnen geben.',
};

/**
 * The message of a 403 for a missing workspace permission, in a language and without the raw
 * permission key (the key travels in `details.permission` for clients that want to react to it).
 */
export function permissionDeniedMessage(language: string = 'en'): string {
  return PERMISSION_DENIED[language === 'de' ? 'de' : 'en'];
}

/** Where a request failed validation (the route parts; `undefined` for a whole input). */
export type ValidationTarget = 'params' | 'query' | 'body';

const VALIDATION_FAILED: Record<MessageLanguage, Record<ValidationTarget | 'input', string>> = {
  en: {
    input: 'Validation failed',
    params: 'Invalid params',
    query: 'Invalid query',
    body: 'Invalid body',
  },
  de: {
    input: 'Die Eingaben sind ungültig',
    params: 'Die Pfadangaben der Anfrage sind ungültig',
    query: 'Die Abfrageparameter sind ungültig',
    body: 'Der Inhalt der Anfrage ist ungültig',
  },
};

/**
 * The top-level message of a `validation_failed` error in a language (the issues carry the
 * details). English stays as before (`Validation failed`, `Invalid body`).
 */
export function validationFailedMessage(language: string = 'en', target?: ValidationTarget): string {
  const lang: MessageLanguage = language === 'de' ? 'de' : 'en';
  return VALIDATION_FAILED[lang][target ?? 'input'];
}

/**
 * Zod issue options with a stable code: `z.string().refine(ok, valueIssue('invalid_time_zone'))`.
 * The message follows the current message language; `issues[].params.code` keeps the code.
 */
export function valueIssue(
  code: ValueErrorCode,
  params: ValueErrorParams = {},
): { params: ValueErrorParams & { code: ValueErrorCode }; error: () => string } {
  return {
    params: { ...params, code },
    error: () => valueErrorMessage(code, params, currentMessageLanguage()),
  };
}

/** The issue shape of Zod 4 this mapping reads (structural, so any Zod build fits). */
type ZodIssueLike = {
  code: string;
  path?: ReadonlyArray<PropertyKey>;
  message?: string;
  expected?: unknown;
  input?: unknown;
  origin?: string;
  format?: string;
  minimum?: number | bigint;
  maximum?: number | bigint;
  keys?: string[];
  params?: Record<string, unknown>;
  /** Issues of each branch of an `invalid_union`. */
  errors?: ReadonlyArray<ReadonlyArray<ZodIssueLike>>;
};

function paramsOf(raw: Record<string, unknown> | undefined): ValueErrorParams | undefined {
  if (!raw) return undefined;
  const out: ValueErrorParams = {};
  for (const [k, v] of Object.entries(raw)) {
    if (k === 'code') continue;
    if (typeof v === 'string' || typeof v === 'number') out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

/** The typed value error of a Zod issue (built-in checks and `valueIssue` refinements). */
export function valueErrorOfZodIssue(issue: ZodIssueLike): ValueError {
  const n = (v: number | bigint | undefined) => (typeof v === 'bigint' ? Number(v) : (v ?? 0));
  const origin = issue.origin ?? '';
  // A code stamped by `valueIssue` or by the API's error map wins.
  const stamped = issue.params?.code;
  if (isValueErrorCode(stamped)) {
    const params = paramsOf(issue.params);
    return params ? { code: stamped, params } : { code: stamped };
  }
  switch (issue.code) {
    case 'custom':
      return { code: 'invalid_value' };
    case 'invalid_type': {
      // Final issues drop the input; Zod's default message still says what was received.
      const missing =
        'input' in issue ? issue.input === undefined : /received undefined/.test(issue.message ?? '');
      if (missing) return { code: 'required' };
      if (issue.expected === 'int') return { code: 'invalid_integer' };
      if (issue.expected === 'number') return { code: 'invalid_number' };
      return { code: 'invalid_type', params: { expected: String(issue.expected ?? 'value') } };
    }
    case 'too_big': {
      const max = n(issue.maximum);
      if (origin === 'string') return { code: 'too_long', params: { max } };
      if (origin === 'array' || origin === 'set') return { code: 'too_many_items', params: { max } };
      return { code: 'too_large', params: { max } };
    }
    case 'too_small': {
      const min = n(issue.minimum);
      if (origin === 'string')
        return min <= 1 ? { code: 'required' } : { code: 'too_short', params: { min } };
      if (origin === 'array' || origin === 'set') return { code: 'too_few_items', params: { min } };
      return { code: 'too_small', params: { min } };
    }
    case 'invalid_format': {
      switch (issue.format) {
        case 'email':
          return { code: 'invalid_email' };
        case 'url':
          return { code: 'invalid_url' };
        case 'date':
          return { code: 'invalid_date' };
        case 'datetime':
          return { code: 'invalid_datetime' };
        case 'time':
          return { code: 'invalid_time' };
        default:
          return { code: 'invalid_format' };
      }
    }
    case 'invalid_value': {
      // An enum or literal that does not match: name the value (the input is only seen while
      // parsing; the code and value are stamped into the final issue's params then).
      const input = issue.input;
      if (typeof input === 'string' || typeof input === 'number')
        return { code: 'unknown_option', params: { value: String(input).slice(0, 80) } };
      return { code: 'invalid_value' };
    }
    case 'unrecognized_keys':
      return { code: 'unknown_key', params: { key: (issue.keys ?? []).join(', ') } };
    case 'invalid_union': {
      // The one branch that got past the type check says what is wrong (`?filter={x` is text
      // but not JSON); when every branch rejected the type, the value is simply not valid.
      const telling = (issue.errors ?? [])
        .map((branch) => branch[0])
        .filter((i): i is ZodIssueLike => !!i && i.code !== 'invalid_type');
      return telling.length === 1 ? valueErrorOfZodIssue(telling[0]!) : { code: 'invalid_value' };
    }
    default:
      return { code: 'invalid_value' };
  }
}

export function formatPersonName(v: PersonName | null | undefined): string {
  if (!v) return '';
  return [v.first, v.last].filter(Boolean).join(' ');
}

export function formatAddress(v: Address | null | undefined): string {
  if (!v) return '';
  return [v.city, v.country].filter(Boolean).join(', ');
}
